import { useMemo, useRef, useState } from 'react';
import ButtonOutline from './ButtonOutline';
import ButtonSolid from './ButtonSolid';

const PREVIEW_COLUMNS = [
  { key: 'model_number', label: 'Model #' },
  { key: 'model_description', label: 'Model Description' },
  { key: 'assembly_number', label: 'Assembly #' },
  { key: 'assembly_type', label: 'Assembly Type' },
  { key: 'assembly_description', label: 'Assembly Description' },
  { key: 'series', label: 'Series', isList: true },
  { key: 'manuals', label: 'Manuals', isList: true },
  { key: 'videos', label: 'Videos', isList: true },
  { key: 'embeddedAssemblies', label: 'Embedded Assemblies', isList: true },
  { key: 'electricalSchematics', label: 'Electrical Schematics', isList: true },
  { key: 'pneumaticSchematic', label: 'Pneumatic Schematic' },
];

function BulkUploadPreview({ fileName, rows, headerErrors, onCancel, onConfirm, isSubmitting }) {
  const hasHeaderErrors = headerErrors.length > 0;
  const errorRowIndexes = useMemo(() => rows.reduce((acc, row, index) => {
    if (row.errors.length > 0) acc.push(index);
    return acc;
  }, []), [rows]);
  const invalidRowCount = errorRowIndexes.length;
  const allRowsInvalid = rows.length > 0 && invalidRowCount === rows.length;

  const [errorCursor, setErrorCursor] = useState(0);
  const rowRefs = useRef({});
  const currentErrorRowIndex = errorRowIndexes[errorCursor];

  const goToError = (direction) => {
    if (errorRowIndexes.length === 0) return;
    const nextCursor = (errorCursor + direction + errorRowIndexes.length) % errorRowIndexes.length;
    setErrorCursor(nextCursor);
    const targetRowIndex = errorRowIndexes[nextCursor];
    rowRefs.current[targetRowIndex]?.scrollIntoView({ block: 'center' });
  };

  return (
    <>
      <h3 className='text-lg font-medium mb-1'>Review CSV Before Upload</h3>
      <p className='text-sm text-gray-500 mb-4 break-all'>{fileName}</p>

      {hasHeaderErrors ? (
        <div className='bg-red-50 border border-red-300 text-red-700 rounded-md p-4 mb-2'>
          <p className='font-medium mb-2'>This CSV is missing required columns and cannot be uploaded:</p>
          <ul className='list-disc list-inside text-sm'>
            {headerErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <div className='flex flex-wrap items-center justify-between gap-3 mb-3'>
            <p className='text-sm text-gray-600'>
              {rows.length} row{rows.length === 1 ? '' : 's'} found.
              {invalidRowCount > 0 && (
                <span className='text-red-600'>
                  {' '}{invalidRowCount} row{invalidRowCount === 1 ? '' : 's'} missing both Model Number and Assembly # and will be skipped.
                </span>
              )}
            </p>
            {invalidRowCount > 0 && (
              <div className='flex items-center gap-2 text-sm'>
                <button
                  type='button'
                  onClick={() => goToError(-1)}
                  className='px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50'
                >
                  ‹ Previous
                </button>
                <span className='text-gray-600 whitespace-nowrap'>Error {errorCursor + 1} of {invalidRowCount}</span>
                <button
                  type='button'
                  onClick={() => goToError(1)}
                  className='px-2 py-1 border border-gray-300 rounded-md hover:bg-gray-50'
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
          <div className='overflow-auto border border-gray-300 rounded-md max-h-[50vh]'>
            <table className='w-full text-sm text-left border-collapse'>
              <thead className='text-gray-500 uppercase text-xs bg-slate-50 border-b border-gray-300 sticky top-0'>
                <tr>
                  <th className='px-3 py-2 border-r border-gray-300'>#</th>
                  {PREVIEW_COLUMNS.map((column) => (
                    <th key={column.key} className='px-3 py-2 border-r border-gray-300 whitespace-nowrap'>{column.label}</th>
                  ))}
                  <th className='px-3 py-2 whitespace-nowrap'>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={index}
                    ref={(el) => { rowRefs.current[index] = el; }}
                    className={`border-b border-gray-200 ${index === currentErrorRowIndex ? 'bg-red-200' : row.errors.length > 0 ? 'bg-red-50' : ''}`}
                  >
                    <td className='px-3 py-2 border-r border-gray-200'>{index + 1}</td>
                    {PREVIEW_COLUMNS.map((column) => {
                      const value = row.mapped[column.key];
                      const display = column.isList ? value.join(', ') : value;
                      return (
                        <td key={column.key} className='px-3 py-2 border-r border-gray-200 whitespace-nowrap'>{display || '-'}</td>
                      );
                    })}
                    <td className='px-3 py-2 whitespace-nowrap'>
                      {row.errors.length > 0 ? (
                        <span className='text-red-600' title={row.errors.join(', ')}>Will be skipped</span>
                      ) : (
                        <span className='text-green-600'>OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className='flex gap-4 justify-center items-center mt-5'>
        <ButtonOutline buttonText='Cancel' onClick={onCancel} disabled={isSubmitting} />
        {!hasHeaderErrors && (
          <ButtonSolid
            buttonText='Confirm & Upload'
            onClick={onConfirm}
            disabled={isSubmitting || rows.length === 0 || allRowsInvalid}
          />
        )}
      </div>
    </>
  );
}

export default BulkUploadPreview;
